import dayJs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayJs.extend(customParseFormat);
/**
 *
 * @typedef {Object} EmploymentStatus
 * @property {string} text - contains the status itself
 * @property {number} value - contains unique identifier
 */

const employmentStatusMap = {
  Employed: 'Employed',
  'Self-employed': 'Self-employed',
  'Maternity Leave': 'On maternity leave',
  'Long-term Illness': 'Not working due to a long term illness',
  'Career Break': 'On a career break',
  Retired: 'Retired',
};

/** this takes an array of employment statuses and adds the displayText property.
 * @param {Array<EmploymentStatus>} employmentStatuses - the list of employment statuses
 * @returns {Array} - list of the statuses with the display text added
 */
const addDisplayText = (employmentStatuses) => employmentStatuses.map((status) => ({
  text: status.text,
  value: status.value,
  displayText: employmentStatusMap[status.text] || status.text,
}));

export default {
  additionalRoles: (state) => state.understandingYouOptions.AdditionalRoles
    .map((e, index) => ({ text: e, value: index })),
  allPaymentOptionsAvailable: (state) => state.enableDirectDebit === true
    && state.paymentOptions.some((o) => o.PaymentMethod === 'Annual' || o.PaymentMethod === 'Monthly'),
  annual: (state) => state.paymentOptions.find((option) => option.PaymentMethod === 'Annual'),
  application: (state) => state.application,
  areaProgress: (state, getters) => getters.getArea.progressValue,
  associateOccupations: (state) => state.understandingYouOptions
    .PrimaryOccupations
    .filter((occ) => occ.PrimaryOccupation !== 'Student' && occ.PrimaryOccupation !== 'Foundation Trainee')
    .map((occ, index) => ({ text: occ.PrimaryOccupation, value: index })),
  card: (state) => state.paymentOptions.find((option) => option.PaymentMethod === 'Card'),
  currentSection: (state) => {
    if (state.sections.length < 1) { return ''; }
    return state.sections[state.selectedView];
  },
  degreeEnds: (state) => state.understandingYouOptions.DegreeEndDates.map((e, index) => ({ text: e, value: index })),
  degreeStarts: (state) => state.understandingYouOptions.DegreeStartDates.map((s, index) => ({ text: s, value: index })),
  desktopSteps: (state) => state.areas.filter((a) => a.stepValue > -1),
  employmentStatuses: (state) => {
    const statuses = state.understandingYouOptions.EmploymentStatuses.map((e, index) => ({ text: e.EmploymentStatus, value: index }));
    return addDisplayText(statuses).filter((e) => e.text !== 'Foundation Trainee' && e.text !== 'Student');
  },
  ftQualifiedIn2025: (state, getters) => getters.isGbFoundationTrainee
    && dayJs(state.application.UnderstandingYou['Foundation Trainee'].assessment).year() === 2025,
  getArea: (state) => {
    const copy = {};
    if (state.currentArea) {
      Object.assign(copy, state.currentArea);
    }
    return copy;
  },
  getAreaName: (state) => (stepValue) => {
    const foundArea = state.areas.filter((area) => area.stepValue === stepValue)[0];
    return foundArea.name;
  },
  getCorrespondence: (state) => {
    const copy = {};
    Object.assign(copy, state.application.Correspondence);
    return copy;
  },
  getInsurance: (state) => {
    const copy = {};
    Object.assign(copy, state.application.Insurance);

    copy.additionalRole = copy?.additionalRole ?? 'Not Applicable';

    return copy;
  },
  getIsOverseas: (state) => {
    if (state.application.Correspondence) {
      return state.application.Correspondence.isOverseas;
    }
    return false;
  },
  getPersonalDetail: (state) => {
    const copy = {};
    Object.assign(copy, state.application.PersonalDetails);
    return copy;
  },
  getUnderstandingYou: (state) => (type) => {
    const copy = {};
    Object.assign(copy, state.application.UnderstandingYou[type]);
    return copy;
  },
  hasNonWorkingStatus: (state, getters) => getters.isMemberTypeApplicant
    && state.nonWorkingStatuses.includes(state.application.UnderstandingYou.Member.employmentStatus),
  isApplicant: (state) => {
    if (state.application && state.application.UserInfo && state.application.UserInfo.userStatus) {
      return state.application.UserInfo.userStatus === 'Applicant';
    }

    return false;
  },
  isFinalSection: (state) => state.selectedView === state.sections.length - 1,
  isFirstSection: (state) => state.selectedView === 0,
  isFoundationTrainee: (state) => state.application.Type === 'Foundation Trainee',
  isLoggedIn: () => document.getElementById('userEmail').value !== '',
  isLoggedInWithNoDob: (state, getters) => getters.isLoggedIn && !(state?.application?.UserInfo?.dob),
  isMember: (state) => {
    if (state.application && state.application.UserInfo && state.application.UserInfo.userStatus) {
      return state.application.UserInfo.userStatus === 'Active';
    }

    return false;
  },
  isMemberTypeApplicant: (state) => state.application.Type === 'Member',
  isOutstandingRenewal: (state) => {
    if (state.application && state.application.UserInfo && state.application.UserInfo.userStatus) {
      return state.application.UserInfo.userStatus === 'Member Outstanding Renewal';
    }

    return false;
  },
  isOverseasFoundationTrainee: (state, getters) => getters.isFoundationTrainee && getters.getIsOverseas,
  isGbFoundationTrainee: (state, getters) => getters.isFoundationTrainee && !getters.getIsOverseas,
  isPli: (state, getters) => getters.isPliCategory && !state.application.Insurance.optedOutOfPli,
  isPliCategory: (state) => state.pliMembershipCategories.includes(state.application.Type),
  isRetiredMember: (state) => state.application.UnderstandingYou.Member.employmentStatus === 'Retired',
  mapExistingPreRegExamDate: (state) => (examDate) => {
    const assessments = state.understandingYouOptions?.TraineeAssessment;
    const matchingDate = assessments.find((a) => a.value === examDate);

    return matchingDate ? matchingDate.text : null;
  },
  memberOccupations: (state) => state.understandingYouOptions
    .PrimaryOccupations
    .filter((occ) => occ.PrimaryOccupation !== 'Student' && occ.PrimaryOccupation !== 'Foundation Trainee')
    .map((occ, index) => ({ text: occ.PrimaryOccupation, value: index })),
  membershipTypes: (state) => state.understandingYouOptions.MembershipTypes.map((member, index) => (
    {
      text: member.Name,
      value: index,
      displayText: member.Name === 'Pre-Registration Trainee' ? 'Foundation Trainee' : member.Name,
    })),
  monthly: (state) => state.paymentOptions.find((option) => option.PaymentMethod === 'Monthly'),
  neverEligible: (state, getters) => !getters.isPliCategory,
  neverEligibleMessage: () => `<h2>RCPharm Professional Liability Insurance</h2>
    <p>Based on the information you have provided, you are not eligible to include RCPharm Professional Liability Insurance within your membership.
    If you believe that there has been a mistake, please contact the RCPharm membership team to amend this.
    </p>
    <p>
    Please note that the regulator (GPhC) requires you to have appropriate professional insurance in order to stay on the register.
    You can find more information about our insurance offering <a href="https://www.rcpharm.org/insurance" target="_blank">here</a>.
    </p>
    <p>
    If your professional circumstances change in the future and you wish to add RCPharm Professional Liability Insurance into your membership,
    you can do so by contacting the RCPharm membership team.
    </p>`,
  notEligibleFromEligibleCategory: (state, getters) => getters.isPliCategory
    && (getters.getIsOverseas || getters.isRetiredMember || getters.ftQualifiedIn2025),
  notEligibleFromEligibleCategoryMessage: () => `<h2>RCPharm Professional Liability Insurance</h2>
    <p>Based on the information you have provided, you are not eligible to include RCPharm Professional Liability Insurance within your membership.
    If you believe that there has been a mistake, please contact the RCPharm membership team to amend this.
    </p>
    <p>
    Please note that the regulator (GPhC) requires you to have appropriate professional insurance in order to stay on the register.
    You can find more information about our insurance offering <a href="https://www.rcpharm.org/insurance" target="_blank">here</a>.
    </p>
    <p>
    If your professional circumstances change in the future and you wish to add RCPharm Professional Liability Insurance into your membership,
    you can do so by contacting the RCPharm membership team.
    </p>`,
  occupations: (state) => state.understandingYouOptions.PrimaryOccupations.map((occ, index) => ({ text: occ.PrimaryOccupation, value: index })),
  optedIntoPli: (state) => state.application.Insurance.optedOutOfPli === false,
  optedIntoPliYesToEither: (state, getters) => getters.optedIntoPli
    && (state.application.Insurance.regulatoryProceedings || state.application.Insurance.claimsInLastFiveYears),
  optedIntoPliYesToEitherConfirmationMessage: () => `<h2>RCPharm Professional Liability Insurance</h2>
    <p>
    Based on the information you have provided (i.e. you have reported previous regulatory/claims history),
    your insurance coverage is subject to review by the insurer before it can be confirmed. You will be contacted
    within the next 5 working days with further information.
    </p>
    <p>
    Once your membership has been confirmed, you will be granted access to a user portal which provides further information about your
    RCPharm Professional Liability Insurance coverage. You can also access more information about RCPharm Professional Liability Insurance
    <a href="https://www.rcpharm.org/insurance" target="_blank"> here</a>.
    You will also receive a Client Information Letter which will be sent to you via email within 2 weeks of your
    confirmation email. This email will not be sent from RPS, but rather will be sent to you from our insurance broker (Aon),
    so please await an email from them.
    </p>`,
  optedIntoPliYesNeitherConfirmationMessage: () => `<h2>RCPharm Professional Liability Insurance</h2>
    <p>Your insurance coverage will be effective from the start of your membership year (i.e. as soon as your membership is activated),
    subject to a satisfactory sanctions check.</p>
    <p>
    You can access additional information about your RCPharm Professional Liability Insurance coverage by clicking
    <a href="https://www.rcpharm.org/insurance" target="_blank"> here</a>.
    You can also access more general information about RCPharm Professional Liability Insurance
    <a href="https://www.rcpharm.org/insurance" target="_blank"> here</a>.
    You will also receive a Client Information Letter which will be sent to you via email within 2 weeks of your confirmation email.
    If you require this more urgently, please contact membership@rcpharm.org.
    The Client Information Letter will not be sent from RCPharm, but rather will be sent to you from our insurance broker (Aon),
    so please await an email from them. Aon can be contacted <a href="https://portal.aon.co.uk/rps/en/home" target="_blank"> here</a>
    if you need to contact a member of their team before you have received your client information letter.
    </p>`,
  optedOutOfPli: (state) => state.application.Insurance.optedOutOfPli === true,
  optedOutOfPliConfirmationMessage: () => `<h2>RCPharm Professional Liability Insurance</h2>
    <p>You have chosen to exclude RCPharm Professional Liability Insurance from your membership.</p>
    <p>Please note that the regulator (GPhC) requires you to have appropriate professional insurance
      in order to stay on the register. If you exclude RCPharm Professional Liability Insurance from your membership, then you must
      ensure that you have appropriate cover elsewhere. If you are not sure whether you require
      RCPharm Professional Liability Insurance, you can find more information about insurance requirements
      <a href="https://www.rcpharm.org/insurance" target="_blank">here</a>.
    </p>
    <p>If you wish to add Professional Liability Insurance into your membership at a later date,
      you can do so by contacting the RCPharm membership team.
    </p>`,
  paymentOptionsDisplay: (state) => {
    const displayOptions = {
      Monthly: {
        displayText: 'Monthly<br/>Direct Debit *',
        displayClass: 'new-options-dd-monthly',
        displayOrder() {
          return state.isMobile ? 'order:1;' : 'order:2;';
        },
        frequencyAmount() {
          const installment = this.option.InstallmentAmount;
          return state.isMobile
            ? `${this.option.Amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })} in total`
            : `${(installment).toLocaleString('en-GB', {
              style: 'currency',
              currency: 'GBP',
              maximumFractionDigits: (Number.isInteger(installment) ? 0 : 2),
            })} / month`;
        },
        frequencyClass() {
          return 'frequency-amount-monthly';
        },
        totalAmount() {
          const installment = this.option.InstallmentAmount;
          return state.isMobile
            ? `${installment.toLocaleString('en-GB', {
              style: 'currency',
              currency: 'GBP',
              maximumFractionDigits: (Number.isInteger(installment) ? 0 : 2),
            })}`
            : `${this.option.Amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })} in total`;
        },
        totalAmountClass() {
          return 'smaller-monthly';
        },
        mobileContainerClass() {
          return 'container-monthly';
        },
        option: null,
      },
      Annual: {
        displayText: 'Annual<br/>Direct Debit *',
        displayClass: 'new-options-dd-annual',
        displayOrder() {
          return state.isMobile ? 'order:2;' : 'order:1;';
        },
        frequencyAmount() {
          return 'A';
        },
        frequencyClass() {
          return 'frequency-amount-annual';
        },
        totalAmount() {
          return this.option.Amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
        },
        totalAmountClass() {
          return 'total-amount-annual';
        },
        mobileContainerClass() {
          return 'container-annual';
        },
        option: null,
      },
      Card: {
        displayText: 'Credit /<br/>Debit Card',
        displayClass: 'new-options-cc',
        displayOrder() {
          return 'order:3;';
        },
        frequencyAmount() {
          return 'A';
        },
        frequencyClass() {
          return 'frequency-amount-cc';
        },
        totalAmount() {
          return this.option.Amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
        },
        totalAmountClass() {
          return 'total-amount-cc';
        },
        mobileContainerClass() {
          return 'container-cc';
        },
        option: null,
      },
    };
    return state.paymentOptions.map((o) => {
      const display = displayOptions[o.PaymentMethod];
      display.option = o;
      return { ...o, ...display };
    }).filter((o) => state.enableDirectDebit === true || o.PaymentMethod === 'Card');
  },
  paymentOptionsInfo: (state) => (state.application.Type === 'Student' && state.application.Correspondence.isOverseas
    ? state.paymentOptionsInfo.paymentOptionsInfoInternationalStudent
    : state.paymentOptionsInfo.paymentOptionsInfo),
  personalDetailWithUserId: (state) => ({ ...state.application.PersonalDetails, userId: state.application?.UserInfo?.userId }),
  qualifiedDates: (state) => state.understandingYouOptions.QualificationDates.map((q, index) => ({ text: q, value: index })),
  returnTitle: (state, getters) => (getters.allPaymentOptionsAvailable ? 'Your payment options' : 'Your payment option'),
  sectors: (state) => state.understandingYouOptions.Sectors.map((sector, index) => ({ text: sector.PrimarySectorTitle, value: index })),
  stepsProgress: (state, getters) => {
    const area = getters.getArea;
    return area.stepValue || 0;
  },
  titles: (state) => state.understandingYouOptions.Titles.map((t, index) => ({ text: t, value: index })),
  traineeAssessments: (state) => state.understandingYouOptions.TraineeAssessment,
  trainingFinishYear: (state) => state.understandingYouOptions.PreRegFinishYears.map((p, index) => ({ text: p, value: index })),
  transactionId: (state, getters) => {
    const result = state.cardPaymentResult;
    const selectedOption = state.paymentOption;

    if (!result) { // assumption - a direct debit instruction was chosen
      if (!selectedOption || !getters.application.UserInfo) return '';

      const { userId } = getters.application.UserInfo;
      const ddType = selectedOption.PaymentMethod;
      return `dd-${ddType}-${userId}`;
    }
    return `card-annual-${result.transactionId}`;
  },
  universities: (state) => {
    const unis = state.understandingYouOptions.Institutions.map((i) => ({
      text: i.Name,
      value: i.InstituteId,
    }));

    unis.push({ text: 'Other', value: 0 });

    return unis;
  },
};
